import { pathOr } from 'ramda'
import { delay, END, eventChannel } from 'redux-saga'
import { call, put, select, take, takeEvery } from 'redux-saga/effects'
import * as webeid from '@web-eid/web-eid-library/web-eid'

import { resetPasswordFailed, resetPasswordSuccess, setAuth, startSocialAuth } from './actions'
import * as Actions from './consts'

import {
  activate as activateApi,
  challenge,
  checkStatus as checkStatusApi,
  recover as recoverApi,
  resetPassword,
  signin as signinApi,
  signout as signoutApi,
  signup as signupApi,
  verify,
  checkSession,
} from './api'
import { translate } from '../../modules/Common/Translator'
import { knownErrors } from '../../modules/Common/known-errors'
import messages from '../../consts/messages'
import { getCurrentReservationId } from '../reservation/selectors'
import { goToPage, setReservationCustomerInfo } from '../../actions'

const UNAUTHORIZED = 401

const AUTH_MESSAGES_DISPLAY_TIME = 10000
const customDelay = (ms) => new Promise((res) => setTimeout(res, ms))

function* logoutIfUnauthorized(action) {
  const status = pathOr('', ['payload', 'status'], action)

  if (status === UNAUTHORIZED) {
    yield put({ type: Actions.SIGNOUT_SUCCESS })
  }
}

function* clearAuthMessages() {
  yield call(customDelay, AUTH_MESSAGES_DISPLAY_TIME)
  yield put(setAuth({ error: '', message: '' }))
}

function* joinProcess({ payload }) {
  try {
    // join with be enpoints
    yield put({ type: Actions.JOIN_SUCCESS, payload })
  } catch (error) {
    const { data = {} } = error

    yield put({
      type: Actions.JOIN_FAILED,
      payload: { ...error, message: data.message || error.message },
    })
  }
}

function* smartIdAuthenticateProcess({ IDCode, CountryCode }) {
  const { verificationCode, sessionId } = yield call(signinApi, { source: 'smartId', IDCode, CountryCode })

  return eventChannel((emit) => {
    setImmediate(() =>
      emit({
        type: 'INIT',
        payload: { verificationCode, sessionId },
      })
    )

    const timer = setInterval(() => {
      checkSession(sessionId)
        .then((res) => {
          if (res.sessionState === 'COMPLETED') {
            emit({ type: 'READY', payload: res })
            emit(END)
            return
          }
          emit({ type: 'TICK', payload: res })
        })
        .catch((err) => {
          emit({ type: 'ERROR', payload: err })
          emit(END)
        })
    }, 5000)

    return () => {
      clearInterval(timer)
    }
  })
}

function* smartIdAuth(payload) {
  const SOURCE = 'smartIdAuth'

  try {
    const channel = yield call(smartIdAuthenticateProcess, payload)
    let cnt = 0

    while (true) {
      const channelMessage = yield take(channel)
      console.log('consumer: ', channelMessage)

      if (channelMessage.type === 'INIT') {
        yield put({
          type: Actions.VERIFICATION_CODE_RECEIVED,
          payload: { challengeID: channelMessage.payload.verificationCode },
        })
      } else if (channelMessage.type === 'ERROR') {
        yield put({
          type: Actions.SIGNIN_FAILED,
          payload: { message: translate(messages.signSmartIDLoginInterrupt), source: SOURCE },
        })
      } else if (channelMessage.type === 'READY') {
        const { token, ...user } = channelMessage.payload.user
        yield put({
          type: Actions.SIGNIN_SUCCESS,
          payload: { user, token, joinToken: payload.joinToken },
        })
      }

      cnt += 1
      if (cnt > 100) {
        // 5-minute timeout
        break
      }
    }

    console.log('consumer: closing channel')

    const lastAction = yield take(channel)
    channel.close()

    if (lastAction.type !== 'READY') {
      yield put({
        type: Actions.SIGNIN_FAILED,
        payload: { message: translate(messages.signSmartIDLoginInterrupt), source: SOURCE },
      })
    }
  } catch (generalError) {
    if (generalError.code === 'SKA1') {
      yield put({
        type: Actions.SIGNIN_FAILED,
        payload: { message: translate(messages.signSmartIDLoginInterrupt), source: SOURCE },
      })
    } else {
      yield put({
        type: Actions.SIGNIN_FAILED,
        payload: generalError,
      })
    }
  }
}

function* setReservationOwnerOnSignIn() {
  const reservationId = yield select(getCurrentReservationId)
  if (reservationId) {
    yield delay(900)
    yield put(setReservationCustomerInfo())
  }
}

function* mobileIdAuth(payload) {
  const { verificationCode, authenticationHash, personalIdentificationNumber, phoneNumber } = yield call(
    signinApi,
    payload
  )

  yield put({
    type: Actions.VERIFICATION_CODE_RECEIVED,
    payload: { challengeID: verificationCode },
  })

  const userAccount = yield call(checkStatusApi, {
    ...{ authenticationHash, personalIdentificationNumber, phoneNumber },
    source: 'mobilId',
  })

  const { token, ...user } = userAccount

  if (token) {
    yield put({
      type: Actions.SIGNIN_SUCCESS,
      payload: {
        user,
        token,
        joinToken: payload.joinToken,
      },
    })
  } else {
    console.error(userAccount)
    throw new Error('This user session does not contain a token')
  }
}

function* signinProcess({ payload }) {
  try {
    if (payload.social) {
      yield put(startSocialAuth(payload))
      return
    }

    if (payload.source.includes('smartId')) {
      yield call(smartIdAuth, payload)
      return
    }

    if (payload.source.includes('cardId')) {
      // const { token, ...user } = yield call(idCartCertificate, payload)

      const auth = (nonce) => webeid.authenticate(nonce, { lang: 'et' })

      try {
        const { sessionId, nonce } = yield call(challenge)
        const authToken = yield call(auth, nonce)
        const { token, ...user } = yield call(verify, { sessionId, authToken })

        yield put({
          type: Actions.SIGNIN_SUCCESS,
          payload: { user, token, joinToken: payload.joinToken },
        })
      } catch (e) {
        yield put({
          type: Actions.SIGNIN_FAILED,
          payload: { ...e, message: translate(messages.cardIdSignInError) || e.message },
        })
      }

      return
    }

    if (payload.source === 'mobilId') {
      yield mobileIdAuth(payload)
    } else {
      const { token, ...user } = yield call(signinApi, payload)

      yield put({
        type: Actions.SIGNIN_SUCCESS,
        payload: { user, token, joinToken: payload.joinToken },
      })
    }
  } catch (error) {
    const { data = {} } = error
    const { code } = data
    const { email } = payload

    let translatedMessage = translate(messages[knownErrors[code]], { email })

    if (!knownErrors[code] && payload.source === 'mobilId') {
      translatedMessage = translate(messages.mobileIdSignInError)
    }

    if (payload.source === 'cardId') {
      translatedMessage = translate(messages.cardIdSignInError)
    }

    yield put({
      type: Actions.SIGNIN_FAILED,
      payload: { ...error, message: translatedMessage || error.message },
    })
  }
}

function* signupProcess({ payload }) {
  try {
    if (payload.social) {
      yield put(startSocialAuth(payload))
      return
    }

    if (payload.source.includes('smartId')) {
      yield call(smartIdAuth, payload)
      return
    }

    if (payload.source === 'mobilId') {
      yield mobileIdAuth(payload)
    } else {
      const { token, ...user } = yield call(signupApi, payload)

      yield put({
        type: Actions.SIGNUP_SUCCESS,
        payload: { user, token },
      })
    }
  } catch (error) {
    const { data = {} } = error
    const { code } = data

    let translatedMessage = translate(messages[knownErrors[code]])

    if (!knownErrors[code] && payload.source === 'mobilId') {
      translatedMessage = translate(messages.mobileIdSignInError)
    }

    yield put({
      type: Actions.SIGNUP_FAILED,
      payload: { ...error, message: translatedMessage || error.message },
    })
  }
}

function* signoutProcess({ payload }) {
  try {
    yield call(signoutApi, payload)
    yield put({ type: Actions.SIGNOUT_SUCCESS })
  } catch (error) {
    const { data = {} } = error

    yield put({
      type: Actions.SIGNOUT_FAILED,
      payload: { ...error, message: data.message || error.message },
    })
  }
}

function* logoutProcess({ payload }) {
  yield call(signoutApi, payload)
  yield put({ type: Actions.LOGOUT_SUCCESS })
}

function* recoverProcess({ payload: { userName, successMessage: message } }) {
  try {
    yield call(recoverApi, { userName })
    yield put({ type: Actions.RECOVER_SUCCESS, payload: { message } })
  } catch (error) {
    const { data = {} } = error

    let { message } = data
    const { code } = data

    if (code && knownErrors[code]) {
      message = translate(messages[knownErrors[code]], { userName })
    } else {
      message = translate(messages.authGeneralError)
    }

    yield put({
      type: Actions.RECOVER_FAILED,
      payload: { ...error, message: message || error.message },
    })
  }
}

function* resetPasswordProcess({ payload }) {
  try {
    const { token } = payload
    const { ...user } = yield call(resetPassword, payload)
    yield put(resetPasswordSuccess({ token, user }))
    yield put(goToPage('/auth/login'))
  } catch (error) {
    const { data = {} } = error

    let { message } = data
    const { code } = data

    if (code && knownErrors[code]) {
      message = translate(messages[knownErrors[code]])
    } else {
      message = translate(messages.authGeneralError)
    }

    yield put(resetPasswordFailed({ ...error, message }))
  }
}

function* activateProcess({ payload }) {
  try {
    const data = yield call(activateApi, payload)
    yield put({ type: Actions.ACTIVATE_SUCCESS, payload: data })
  } catch (error) {
    const { data = {} } = error

    yield put({
      type: Actions.ACTIVATE_FAILED,
      payload: { ...error, message: data.message || error.message },
    })
  }
}

function* watchSignin() {
  yield takeEvery(Actions.SIGNIN, signinProcess)
}

function* watchSignup() {
  yield takeEvery(Actions.SIGNUP, signupProcess)
}

function* watchSignout() {
  yield takeEvery(Actions.SIGNOUT, signoutProcess)
}

function* watchLogout() {
  yield takeEvery(Actions.LOGOUT, logoutProcess)
}

function* watchRecover() {
  yield takeEvery(Actions.RECOVER, recoverProcess)
}

function* watchResetPassword() {
  yield takeEvery(Actions.RESET_PASSWORD, resetPasswordProcess)
}

function* watchActivate() {
  yield takeEvery(Actions.ACTIVATE, activateProcess)
}

function* watchJoin() {
  yield takeEvery(Actions.JOIN, joinProcess)
}

function* failedLogoutWatcher() {
  yield takeEvery(Actions.SIGNOUT_FAILED, logoutIfUnauthorized)
}

function* watchAuthMessages() {
  yield takeEvery(
    [
      Actions.JOIN_FAILED,
      Actions.SIGNIN_FAILED,
      Actions.SIGNUP_FAILED,
      Actions.SIGNOUT_FAILED,
      Actions.RECOVER_FAILED,
      Actions.RECOVER_SUCCESS,
      Actions.ACTIVATE_SUCCESS,
      Actions.ACTIVATE_FAILED,
      Actions.RESET_PASSWORD_FAILED,
      Actions.RESET_PASSWORD_SUCCESS,
      Actions.CARD_ID_PLUGIN_FAILED,
      Actions.TOKEN_CHECK_ERROR,
    ],
    clearAuthMessages
  )
}

function* watchAuthSuccess() {
  yield takeEvery(Actions.SIGNIN_SUCCESS, setReservationOwnerOnSignIn)
}

export function* authSaga() {
  yield [
    watchSignin(),
    watchSignup(),
    watchSignout(),
    watchLogout(),
    watchRecover(),
    watchActivate(),
    watchAuthMessages(),
    watchJoin(),
    failedLogoutWatcher(),
    watchAuthSuccess(),
    watchResetPassword(),
  ]
}
